/* ExportsReportsTab — single home for all .xlsx workbook downloads.
 * Phase 1: Raw Data + Finance Workbook are live. Phase 2 workbooks are listed
 * with "Coming soon" so users see the full roadmap without clicking blind.
 *
 * Every download honors optional Fund + Campaign filters so an admin can
 * generate a campaign-specific workbook in one click (e.g. just the data
 * for "Kickoff Fundraiser 2026"). The download is initiated via fetch +
 * blob so the cookie auth is sent automatically and the browser names the
 * file from Content-Disposition.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  FileSpreadsheet, Loader2, Download, Sparkles, FlaskConical, ScrollText,
  Scale, Trophy, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_1 = [
  {
    id: 'raw-data',
    title: 'Raw Data Export',
    purpose: 'Accountant use, backups, imports.',
    icon: FlaskConical,
    tabs: ['Donations', 'Expenses', 'Reimbursements', 'Journal Entries', 'Travel Logs', 'Funds', 'Chart of Accounts', 'Campaigns'],
    endpoint: '/api/admin/exports/raw-data.xlsx',
    available: true,
  },
  {
    id: 'finance-workbook',
    title: 'Finance Workbook',
    purpose: 'Internal finance management with live formulas — totals update when rows are added.',
    icon: FileSpreadsheet,
    tabs: ['Dashboard Summary', 'Donations', 'Expenses', 'Reimbursements', 'Funds', 'Journal Entries'],
    endpoint: '/api/admin/exports/finance-workbook.xlsx',
    available: true,
    formulas: ['=SUM', '=SUMIFS', '=COUNTIF', '=SUBTOTAL'],
  },
  {
    id: 'treasurer',
    title: 'Treasurer Report',
    purpose: 'Board-meeting deck: revenue by fund, expenses by category, fund balances, treasurer notes.',
    icon: ScrollText,
    tabs: ['Headline Numbers', 'Revenue by Fund', 'Expenses by Category', 'Fund Balances', 'Pending Reimbursements', 'Missing Receipts', 'Treasurer Notes'],
    endpoint: '/api/admin/exports/treasurer-report.xlsx',
    available: true,
    supportsDates: true,
  },
  {
    id: 'statement-of-activities',
    title: 'Statement of Activities',
    purpose: 'FASB ASC 958 two-column statement for IRS Form 990 and grant applications.',
    icon: ScrollText,
    tabs: ['Without Donor Restrictions', 'With Donor Restrictions', 'Total Revenue', 'Program / Admin / Fundraising Expenses', 'Change in Net Assets'],
    endpoint: '/api/admin/exports/statement-of-activities.xlsx',
    available: true,
    supportsDates: true,
  },
  {
    id: 'balance-sheet',
    title: 'Balance Sheet',
    purpose: 'Snapshot of Assets / Liabilities / Net Assets at any as-of date.',
    icon: Scale,
    tabs: ['Assets', 'Liabilities', 'Net Assets', 'Equation Check'],
    endpoint: '/api/admin/exports/balance-sheet.xlsx',
    available: true,
    supportsAsOf: true,
  },
  {
    id: 'kickoff',
    title: 'Kickoff Fundraiser Report',
    purpose: 'Per-event or per-campaign P&L: donations, merch sales, RSVP tickets, event expenses, marketing, reimbursements, fund allocations.',
    icon: Trophy,
    tabs: ['P&L Summary', 'Donations', 'Merchandise Sales', 'RSVP / Ticket Revenue', 'Event Expenses', 'Marketing Expenses', 'Reimbursements'],
    endpoint: '/api/admin/exports/kickoff-fundraiser.xlsx',
    available: true,
    supportsEvent: true,
  },
];

const PHASE_2 = [];

const ExportsReportsTab = () => {
  const [funds, setFunds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const firstOfYear = () => `${new Date().getUTCFullYear()}-01-01`;
  const [filters, setFilters] = useState({
    fund_id: '', campaign_id: '', event_id: '',
    date_from: firstOfYear(), date_to: todayIso(),
    as_of: todayIso(),
  });
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [f, c, ev] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/funds`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/campaigns`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/events`, { withCredentials: true })
          .catch(() => ({ data: { events: [] } })),
      ]);
      setFunds(f.data?.funds || []);
      setCampaigns(c.data?.campaigns || []);
      setEvents(ev.data?.events || ev.data || []);
    } catch (e) { /* swallow */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (item) => {
    if (!item.available) return;
    setDownloadingId(item.id); setError('');
    try {
      const params = new URLSearchParams();
      if (filters.fund_id) params.set('fund_id', filters.fund_id);
      if (filters.campaign_id) params.set('campaign_id', filters.campaign_id);
      if (item.supportsEvent && filters.event_id) params.set('event_id', filters.event_id);
      if (item.supportsDates) {
        if (filters.date_from) params.set('date_from', filters.date_from);
        if (filters.date_to) params.set('date_to', filters.date_to);
      }
      if (item.supportsAsOf && filters.as_of) params.set('as_of', filters.as_of);
      const url = `${BACKEND_URL}${item.endpoint}${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const dispo = res.headers.get('content-disposition') || '';
      const m = /filename=([^;]+)/i.exec(dispo);
      const fname = (m ? m[1] : `${item.id}.xlsx`).replace(/["']/g, '').trim();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { setError(e.message || 'Download failed.'); }
    setDownloadingId(null);
  };

  return (
    <div className="space-y-4" data-testid="exports-reports-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#BA1826]" /> Exports & Reports
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            One-click Excel (.xlsx) workbooks with live formulas, tables, filters, and board-ready
            formatting. Optional Fund and Campaign filters scope every export.
          </p>
        </div>
        <button onClick={load} className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
          data-testid="exports-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 space-y-2"
        data-testid="exports-filters">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mr-1">
            Scope:
          </span>
          <select value={filters.fund_id}
            onChange={(e) => setFilters((f) => ({ ...f, fund_id: e.target.value }))}
            className="text-xs px-2 py-1.5 border border-[#E5E7EB] rounded w-52"
            data-testid="exports-fund-filter">
            <option value="">All funds</option>
            {funds.map((f) => (
              <option key={f.id} value={f.id}>{f.code} — {f.name}</option>
            ))}
          </select>
          <select value={filters.campaign_id}
            onChange={(e) => setFilters((f) => ({ ...f, campaign_id: e.target.value }))}
            className="text-xs px-2 py-1.5 border border-[#E5E7EB] rounded w-52"
            data-testid="exports-campaign-filter">
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
          <select value={filters.event_id}
            onChange={(e) => setFilters((f) => ({ ...f, event_id: e.target.value }))}
            className="text-xs px-2 py-1.5 border border-[#E5E7EB] rounded w-52"
            data-testid="exports-event-filter">
            <option value="">All events (Kickoff only)</option>
            {(events || []).map((ev) => (
              <option key={ev.id || ev._id} value={ev.id || ev._id}>{ev.title || ev.name || ev.id}</option>
            ))}
          </select>
          {(filters.fund_id || filters.campaign_id || filters.event_id) && (
            <button onClick={() => setFilters((f) => ({ ...f, fund_id: '', campaign_id: '', event_id: '' }))}
              className="text-[10px] text-[#9CA3AF] underline"
              data-testid="exports-filters-clear">Clear scope</button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mr-1">
            Period:
          </span>
          <input type="date" value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="text-xs px-2 py-1.5 border border-[#E5E7EB] rounded"
            data-testid="exports-date-from" />
          <span className="text-[10px] text-[#9CA3AF]">→</span>
          <input type="date" value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="text-xs px-2 py-1.5 border border-[#E5E7EB] rounded"
            data-testid="exports-date-to" />
          <span className="text-[10px] text-[#9CA3AF] ml-3 mr-1">As of (Balance Sheet):</span>
          <input type="date" value={filters.as_of}
            onChange={(e) => setFilters((f) => ({ ...f, as_of: e.target.value }))}
            className="text-xs px-2 py-1.5 border border-[#E5E7EB] rounded"
            data-testid="exports-as-of" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

      {/* Phase 1 — available now */}
      <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 inline-flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> All workbooks ready
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PHASE_1.map((item) => (
          <ExportCard key={item.id} item={item}
            downloading={downloadingId === item.id}
            onDownload={() => download(item)} />
        ))}
      </div>
    </div>
  );
};

const ExportCard = ({ item, downloading, onDownload }) => {
  const Icon = item.icon;
  const isReady = !!item.available;
  return (
    <div className={`bg-white border rounded-lg p-3 ${isReady ? 'border-[#E5E7EB]' : 'border-dashed border-[#E5E7EB] opacity-75'}`}
      data-testid={`export-card-${item.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className={`shrink-0 w-8 h-8 rounded ${isReady ? 'bg-[#FFF8F8] text-[#BA1826]' : 'bg-[#F1F5F9] text-[#9CA3AF]'} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#0A0A0A]">{item.title}</p>
            <p className="text-[11px] text-[#4B5563] mt-0.5">{item.purpose}</p>
          </div>
        </div>
        {isReady ? (
          <button onClick={onDownload} disabled={downloading}
            className="shrink-0 text-[11px] px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid={`export-btn-${item.id}`}>
            {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            {downloading ? 'Building…' : 'Export Excel'}
          </button>
        ) : (
          <span className="shrink-0 text-[10px] uppercase font-bold text-[#9CA3AF] border border-[#E5E7EB] px-2 py-1 rounded">
            Coming soon
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {item.tabs.map((t) => (
          <span key={t} className="text-[9px] uppercase tracking-wider font-bold text-[#4B5563] bg-[#FAFBFC] border border-[#E5E7EB] rounded px-1.5 py-0.5">
            {t}
          </span>
        ))}
      </div>
      {item.formulas && (
        <p className="text-[10px] text-[#9CA3AF] mt-1.5">
          Formulas: <span className="font-mono">{item.formulas.join(' · ')}</span>
        </p>
      )}
    </div>
  );
};

export default ExportsReportsTab;
